import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { setDestroy } from "components/modalx/modalx.slice";
import { btnDangerCls, btnSecondaryCls } from "components/tw/classes";
import { deleteDraftInvoice } from "services/invoices.service";
import { invoiceErrorMessage } from "utils/invoice-errors";
import { formatPeriodMonth } from "features/billing-period/billing-period";

type Props = {
  invoiceId: string;
  customerName?: string;
  /** Called after the draft was deleted, so the caller can refresh its list. */
  onDone?: () => void;
};

/**
 * Confirmation for deleting a draft invoice — a modalX component.
 *
 * Worth confirming for one reason beyond "it deletes something": the order
 * behind the draft is currently hidden from the customer's "Create invoice"
 * list, and deleting is what puts it back. Say that, so the user knows this is
 * the way to start over rather than a dead end.
 */
export default function DeleteDraftInvoiceDialog({
  invoiceId,
  customerName,
  onDone,
}: Props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { companyId } = useParams<{ companyId: string }>();
  const [deleting, setDeleting] = useState(false);

  const close = () => dispatch(setDestroy());

  const handleDelete = async () => {
    if (!companyId) return;
    setDeleting(true);
    try {
      const result = await deleteDraftInvoice({ companyId, invoiceId });
      // A recurring order's draft: deleting it now also records the period as
      // skipped (invoices/delete-draft.js), so the cron's catch-up no longer
      // silently re-creates the very draft the user just removed — say so,
      // since that is a real behaviour change from a plain delete.
      if (result?.skipped && result?.periodFrom) {
        toast.success(
          t("draft_invoice_deleted_period_skipped", {
            defaultValue:
              "Deleted. {{month}} will not be invoiced for this order.",
            month: formatPeriodMonth(result.periodFrom),
          })
        );
      } else {
        toast.success(
          t("draft_invoice_deleted", { defaultValue: "Draft invoice deleted" })
        );
      }
      onDone?.();
      close();
    } catch (e: any) {
      toast.error(invoiceErrorMessage(e, t));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-zinc-700 dark:text-zinc-200">
        {/* Two sentences, not one with a fallback noun: Lithuanian declines the
            recipient, so "klientui ({{customer}})" with the customer missing
            read "klientui (klientui)". */}
        {customerName
          ? t("delete_draft_invoice_confirm", {
              defaultValue:
                "This draft has no invoice number and nothing has been sent to {{customer}}. Its order becomes available again for creating an invoice.",
              customer: customerName,
            })
          : t("delete_draft_invoice_confirm_no_customer", {
              defaultValue:
                "This draft has no invoice number and nothing has been sent to the customer. Its order becomes available again for creating an invoice.",
            })}
      </p>
      <div className="flex justify-end gap-2">
        <button type="button" className={btnSecondaryCls} onClick={close} disabled={deleting}>
          {t("cancel", { defaultValue: "Cancel" })}
        </button>
        <button
          type="button"
          className={btnDangerCls}
          onClick={handleDelete}
          disabled={deleting}
        >
          {deleting ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : null}
          {t("delete", { defaultValue: "Delete" })}
        </button>
      </div>
    </div>
  );
}
